import { ThemeSettings, useAuthContext, useThemeContext } from '@/common';
import { lazy, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes as ReactRoutes } from 'react-router-dom';
import VerticalLayout from '@/layouts/Vertical';
import HorizontalLayout from '@/layouts/Horizontal';
import Root from './Root';
import Productivity from '@/pages/productivity';
import AuraChatFabButton from '@/components/Commons/AuraChatFabButton';

/**
 * routes import
 */
const Dashboard = lazy(() => import('../pages/dashboard'));
const Devices = lazy(() => import('../pages/devices'));
const Predictions = lazy(() => import('../pages/predictions'));
const Alerts = lazy(() => import('../pages/incidences'));
const MyAura = lazy(() => import('../pages/my-aura'));
const Admin = lazy(() => import('../pages/admin'));
const AdminDevices = lazy(() => import('../pages/admin_devices'));
const Companies = lazy(() => import('../pages/companies'));
const Error404Alt = lazy(() => import('../pages/otherpages/Error404Alt'));
const Elia = lazy(() => import('../pages/dashboard/Elia'));
const MyAccount = lazy(() => import('../pages/profile/MyAccount'));
const Legals = lazy(() => import('../pages/legals'));
const ChangePassword = lazy(() => import('../pages/change_password'));

export default function ProtectedRoutes() {
	const { settings } = useThemeContext();
	const Layout =
		settings.layout.type == ThemeSettings.layout.type.vertical
			? VerticalLayout
			: HorizontalLayout;

	const { user, checkSession, checkingSession } = useAuthContext();

	// Only trigger session check on component mount
	useEffect(() => {
		if (user) {
			checkSession(user.token);
		}
	}, [checkSession, user]);

	// Move useMemo to the top level, outside of any conditions

	// Add this function outside the component
	const getTimeBasedGreeting = () => {
		const hour = new Date().getHours();

		if (hour >= 5 && hour < 12) {
			return 'Buenos días';
		} else if (hour >= 12 && hour < 20) {
			return 'Buenas tardes';
		} else {
			return 'Buenas noches';
		}
	};

	const greeting = useMemo(() => getTimeBasedGreeting(), []);

	// Inside ProtectedRoutes component
	if (checkingSession) {
		return (
			<div className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light">
				<div className="text-center">
					<div className="farm-scene mb-4">
						<div className="windmill">
							<div className="roof fs-4">^</div>
							<div className="blades">
								<span className="fs-3">╱</span>
								<span className="fs-3">╲</span>
								<span className="fs-3">╱</span>
								<span className="fs-3">╲</span>
							</div>
							<div className="base fs-4">║</div>
						</div>
						<div className="ground mt-2">
							<div className="crops fs-5 text-success">♠ ♠ ♠ ♠ ♠ ♠</div>
						</div>
					</div>
					<div className="greeting-text h3 mb-3">{greeting}</div>
					<p className="loading-text text-muted fs-5">Preparando tu Granja...</p>
				</div>
			</div>
		);
	}

	return user && !checkingSession ? (
		<>
			<ReactRoutes>
				<Route path="/*" element={<Layout />}>
					<Route index element={<Root />} />
					<Route path="legals" element={<Legals />} />
					<Route path="dashboard/*" element={<Dashboard />} />
					<Route path="productivity/*" element={<Productivity />} />
					<Route path="devices/*" element={<Devices />} />
					<Route path="predicts/*" element={<Predictions />} />
					<Route path="alerts/*" element={<Alerts />} />
					<Route path="my-aura/*" element={<MyAura />} />
					<Route path="admin/*" element={<Admin />} />
					<Route path="admin_devices/*" element={<AdminDevices />} />
					<Route path="companies/*" element={<Companies />} />
					<Route path="elia/*" element={<Elia />} />
					<Route path="profile" element={<MyAccount />} />
					<Route path="change-otp" element={<ChangePassword />} />
					<Route path="*" element={<Error404Alt />} />
				</Route>
			</ReactRoutes>
			<div
				style={{
					position: 'fixed',
					bottom: '2rem',
					right: '2rem',
					zIndex: 1000,
				}}
			>
				<AuraChatFabButton />
			</div>
		</>
	) : (
		<Navigate to="/account/login" replace />
	);
}
